<template>
    <DashboardLayout title="Edit Service">
        <div class="max-w-3xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
            <div class="bg-white shadow rounded-lg">
                <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 class="text-xl font-semibold text-gray-900">Edit Service</h2>
                    <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Update the service information.</p>
                </div>

                <form @submit.prevent="submit" class="px-6 py-6 space-y-6">
                    <!-- Category Field -->
                    <div>
                        <label for="service_category_id" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Category <span class="text-red-500">*</span>
                        </label>
                        <select
                            id="service_category_id"
                            v-model="form.service_category_id"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300': form.errors.service_category_id }"
                        >
                            <option value="">Select a category</option>
                            <option v-for="category in categories" :key="category.id" :value="category.id">
                                {{ category.name }}
                            </option>
                        </select>
                        <p v-if="form.errors.service_category_id" class="mt-1 text-sm text-red-600">
                            {{ form.errors.service_category_id }}
                        </p>
                    </div>

                    <!-- Name Field -->
                    <div>
                        <label for="name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Service Name <span class="text-red-500">*</span>
                        </label>
                        <input
                            id="name"
                            v-model="form.name"
                            type="text"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300': form.errors.name }"
                        />
                        <p v-if="form.errors.name" class="mt-1 text-sm text-red-600">
                            {{ form.errors.name }}
                        </p>
                    </div>

                    <!-- Description Field -->
                    <div>
                        <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Description
                        </label>
                        <textarea
                            id="description"
                            v-model="form.description"
                            rows="3"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300': form.errors.description }"
                        ></textarea>
                        <p v-if="form.errors.description" class="mt-1 text-sm text-red-600">
                            {{ form.errors.description }}
                        </p>
                    </div>

                    <!-- Estimated Duration Field -->
                    <div>
                        <label for="estimated_duration_hours" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Estimated Duration (hours)
                        </label>
                        <input
                            id="estimated_duration_hours"
                            v-model.number="form.estimated_duration_hours"
                            type="number"
                            min="0"
                            step="0.01"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300': form.errors.estimated_duration_hours }"
                        />
                        <p class="mt-1 text-sm text-gray-500">Optional estimate for how long this service typically takes.</p>
                        <p v-if="form.errors.estimated_duration_hours" class="mt-1 text-sm text-red-600">
                            {{ form.errors.estimated_duration_hours }}
                        </p>
                    </div>

                    <!-- Estimated Cost Field -->
                    <div>
                        <label for="estimated_cost" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Estimated Cost ({{ currencySymbol }})
                        </label>
                        <input
                            id="estimated_cost"
                            v-model.number="form.estimated_cost"
                            type="number"
                            min="0"
                            step="0.01"
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                            :class="{ 'border-red-300': form.errors.estimated_cost }"
                        />
                        <p class="mt-1 text-sm text-gray-500">Optional estimate for the cost of this service.</p>
                        <p v-if="form.errors.estimated_cost" class="mt-1 text-sm text-red-600">
                            {{ form.errors.estimated_cost }}
                        </p>
                    </div>

                    <!-- Is Active Checkbox -->
                    <div class="flex items-start">
                        <div class="flex items-center h-5">
                            <input
                                id="is_active"
                                v-model="form.is_active"
                                type="checkbox"
                                class="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                        </div>
                        <div class="ml-3 text-sm">
                            <label for="is_active" class="font-medium text-gray-700">Active</label>
                            <p class="text-gray-500">Make this service available for use.</p>
                        </div>
                    </div>

                    <!-- Form Actions -->
                    <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                        <Link
                            :href="route('settings.services.index')"
                            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Cancel
                        </Link>
                        <button
                            type="submit"
                            :disabled="form.processing"
                            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <span v-if="form.processing">Updating...</span>
                            <span v-else>Update Service</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </DashboardLayout>
</template>

<script setup>
import { useForm, Link } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import { useToast } from '@/Composables/useToast';
import { useCurrency } from '@/Composables/useCurrency';

const props = defineProps({
    service: {
        type: Object,
        required: true
    },
    categories: {
        type: Array,
        required: true
    }
});

const toast = useToast();
const { currencySymbol } = useCurrency();

const form = useForm({
    service_category_id: props.service.service_category_id,
    name: props.service.name,
    description: props.service.description || '',
    estimated_duration_hours: props.service.estimated_duration_hours,
    estimated_cost: props.service.estimated_cost,
    is_active: props.service.is_active,
    sort_order: props.service.sort_order
});

const submit = () => {
    form.put(route('settings.services.update', props.service.id), {
        onSuccess: () => {
            toast.success('Service updated successfully!');
        },
        onError: () => {
            toast.error('Failed to update service. Please check the form for errors.');
        }
    });
};
</script>
